// Copyright (c) 2019 Comsearch, Inc., All rights reserved

import React, { useCallback } from 'react'
import { array, element, func, object, oneOfType, string } from 'prop-types'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { DataTable } from 'components'
import getNestedByString from 'helpers/getNestedByString'

import {
  handleSort,
  useCollateData,
} from './helpers'

// NOTE when calling this component, it requires:
// columns - an array of columns for the <DataTable />
// dataPath - a string which points to where the data is stored in redux
//    eg. 'projectTracker.workOrders.data'
// handleUpdateSort - custom handler for updating sort.
// filterAndSortData - func which takes data and formValues and returns the filtered/sorted data to display.

// optional props:
// handleRowClick - custom handler for row clicks

const FilterAndDisplay = props => {
  const {
    children,
    columns,
    customHeader,
    dataPath,
    handleRowClick,
    handleSort,
    formValues,
    rowClass,
    uniqueKey,
  } = props

  const tableSort = formValues?.tableSort || null

  const handleSorter = useCallback(col => (
    handleSort(dataPath, col)
  ), [dataPath])

  const {
    filteredAndSortedData,
    paginatedData,
  } = useCollateData(props)

  // NOTE - we're cloning children here so we can pass down the dataPath as form,
  // as well as the filteredAndSortedData as filteredAndSortedData.
  // This way, we only have to pass the value once (to FilterAndDisplay)
  // and not have to pass it also down to the form element. (children in the return below)

  return (
    <>
      {
        React.Children.map(children, child =>
          React.cloneElement(
            child,
            {
              columns,
              filteredAndSortedData,
              form: dataPath,
            },
          )
        )
      }
      <DataTable
        columns={columns}
        customHeader={customHeader}
        data={paginatedData}
        handleRowClick={handleRowClick}
        handleSort={handleSorter}
        rowClass={rowClass}
        tableSort={tableSort}
        uniqueKey={uniqueKey} />
    </>
  )
}

FilterAndDisplay.propTypes = {
  children: oneOfType([
    array.isRequired,
    element.isRequired,
  ]),
  columns: array.isRequired,
  customHeader: func,
  filterAndSortData: func.isRequired,
  formValues: object,
  handleRowClick: func,
  rowClass: oneOfType([
    func,
    string,
  ]),
  uniqueKey: string.isRequired,
}

const mapStateToProps = (state, { dataPath }) => {
  const data = getNestedByString(state, dataPath)
  const formValues = getNestedByString(state.form, dataPath)?.values || null

  return {
    data,
    formValues,
  }
}

const mapDispatchToProps = (dispatch, props) => (
  bindActionCreators({
    handleSort,
  }, dispatch)
)

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(FilterAndDisplay)
